/**
 * Public marketing landing page (`/`).
 *
 * The Phase 2 renderer was orphaned when the SPA moved to React; this is the same content as a
 * real component, with a CTA that depends on the session.
 */

import { Link } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";

const PILLARS = [
  {
    badge: "Human-in-the-Loop VNC",
    tone: "tj-badge-green",
    title: "Seamless takeover & handback",
    body: "Designed for financial, regulatory and government portals. Complete login and hardware 2FA inside an encrypted VNC stream, then hand control back while the session cookies are sealed for future runs.",
  },
  {
    badge: "TensileAuth cross-app PKI",
    tone: "tj-badge-amber",
    title: "One Passkey for every tool",
    body: "Passwordless sign-in bound to tensilelabs.com with WebAuthn PRF key wrapping. Your Master Identity Key derives an isolated HKDF subkey per app, on every authorized device.",
  },
  {
    badge: "DeepSeek thinking + defense",
    tone: "tj-badge-green",
    title: "Tiered reasoning, zero-leak redaction",
    body: "DeepSeek Pro (high-effort thinking) designs and reviews workflows; DeepSeek Flash (medium effort) executes them behind a capability-restricted ingress gateway.",
  },
] as const;

export function LandingPage() {
  const { session } = useAuth();
  return (
    <main className="tj-container" data-testid="landing-page">
      <section className="mx-auto max-w-3xl py-10 text-center">
        <span className="tj-badge tj-badge-amber mb-4 inline-block">
          Tensile Labs • Human-supervised browser automation
        </span>
        <h1 className="serif-heading text-3xl leading-tight sm:text-5xl">
          Automate sensitive web workflows without sharing your credentials.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-[var(--tj-muted)] sm:text-lg">
          Task Jinn pairs a stealth CloakBrowser agent with an encrypted live VNC takeover switch.
          Step in for passwords and 2FA, hand control back to the agent, and keep every secret
          encrypted under your personal TensileAuth Passkey PKI.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {session ? (
            <>
              <Link to="/dashboard" className="tj-btn tj-btn-primary inline-block">
                Open the dashboard
              </Link>
              <Link to="/account" className="tj-btn tj-btn-secondary inline-block">
                Account &amp; devices
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="tj-btn tj-btn-primary inline-block">
                Get started with a Passkey
              </Link>
              <Link to="/login" search={{ redirect: "/dashboard" }} className="tj-btn tj-btn-secondary inline-block">
                Sign in to your workspace
              </Link>
            </>
          )}
        </div>
      </section>

      <section className="grid gap-4 pb-12 sm:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((pillar) => (
          <article key={pillar.title} className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-5">
            <span className={`tj-badge ${pillar.tone} mb-3 inline-block`}>{pillar.badge}</span>
            <h2 className="serif-heading text-xl">{pillar.title}</h2>
            <p className="mt-2 text-sm text-[var(--tj-muted)]">{pillar.body}</p>
          </article>
        ))}
      </section>
    </main>
  );
}
